import React, { useState, useEffect, useRef } from 'react';
import { ReactComponent as Upload } from '../../../assets/svg/upload.svg';
import { ReactComponent as Delete } from '../../../assets/svg/delete.svg';
import styled, { css } from 'styled-components';
import { storage } from '../../../firebase';
import { v4 as uuid } from 'uuid';
import AudioPlay from '../../AudioPlay';
import { useAudios } from '../../../hooks/useAudios';

const AudioHandler = ({ audio = undefined, onChange = () => {} }) => {
  const [source, setSource] = useState(audio);
  const audios = useAudios([source]);

  const [loading, setLoading] = useState(false);
  const dialogRef = useRef();

  useEffect(() => {
    setSource(audio);
  }, [audio]);

  useEffect(() => {
    onChange({ name: source });
  }, [source, onChange]);

  const openDialog = () => {
    dialogRef.current.value = '';
    dialogRef.current.click();
  };

  const onSelect = (e) => {
    const files = e.target.files;
    if (files.length === 0) return;
    setLoading(true);
    const file = files[0];
    audioUpload(file)
      .then((name) => setSource(name))
      .finally(() => setLoading(false));
  };

  return (
    <Container>
      <Wrapper loading={loading}>
        <AudioWrapper>
          {source && audios[source] ? <AudioPlay audio={audios[source]} /> : 'No audio'}
        </AudioWrapper>
        <Options>
          {source && (
            <Option backgroundColor='#b0bec5' onClick={() => setSource(undefined)}>
              <OptionIcon as={Delete} />
            </Option>
          )}
          <Option backgroundColor='#42a5f5' onClick={openDialog}>
            <OptionIcon as={Upload} />
          </Option>
        </Options>
      </Wrapper>
      {loading && (
        <LoadingWrapper>
          <Spinner src='loading.gif' />
        </LoadingWrapper>
      )}
      <input hidden ref={dialogRef} type='file' accept='audio/*' onChange={onSelect} />
    </Container>
  );
};

const audioUpload = (file) =>
  new Promise(async (resolve, reject) => {
    const audioId = uuid();
    const ref = storage.ref(`audios/${audioId}`);
    ref
      .put(file)
      .then(() => ref.getDownloadURL())
      .then(() => resolve(audioId))
      .catch(() => reject());
  });

const Container = styled.div`
  position: relative;
  display: inline-block;
`;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  ${(props) =>
    props.loading &&
    css`
      opacity: 0.2;
    `}
`;

const AudioWrapper = styled.div`
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background-color: #f5f5f5;
  border: 1px solid rgba(0, 0, 0, 0.05);
  color: black;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7em;
`;

const Options = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-left: 5px;
`;

const Option = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  cursor: pointer;
  background-color: ${(props) => props.backgroundColor || 'gray'};
  :not(:last-child) {
    margin-bottom: 5px;
  }
`;

const OptionIcon = styled.div`
  fill: ${(props) => props.color || 'white'};
  width: 15px;
  height: 15px;
`;

const LoadingWrapper = styled.div`
  position: absolute;
  top: 0px;
  bottom: 0px;
  left: 0px;
  right: 0px;

  display: flex;
  align-items: center;
  justify-content: center;

  cursor: not-allowed;
`;

const Spinner = styled.img`
  width: 15px;
  height: 15px;
`;

export default AudioHandler;
